<template>
  <div class="kpi-format-picker" ref="pickerContainer">
    <!-- Label -->
    <div class="picker-field-label">{{ displayLabel }}</div>

    <!-- Closed State / Trigger -->
    <div
      class="picker-trigger"
      :class="{
        'picker-trigger--open': isMenuOpen,
        'picker-trigger--disabled': disabled,
      }"
      role="combobox"
      aria-haspopup="listbox"
      :aria-label="displayLabel"
      :aria-expanded="isMenuOpen ? 'true' : 'false'"
      :aria-disabled="disabled ? 'true' : 'false'"
      :tabindex="disabled ? -1 : 0"
      @click="toggleDropdown"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <div class="picker-content">
        <div class="picker-input-content">
          <span v-if="isMenuOpen" class="picker-placeholder">{{
            placeholder
          }}</span>
          <span ref="labelElement" class="picker-label">{{
            categoryLabel
          }}</span>
        </div>
      </div>
      <v-icon
        class="picker-chevron"
        :class="{ 'picker-chevron--rotated': isMenuOpen }"
      >
        mdi-chevron-down
      </v-icon>
    </div>

    <!-- Dropdown Menu -->
    <div v-if="isMenuOpen" class="picker-dropdown">
      <div class="picker-menu" role="listbox" :aria-label="displayLabel">
        <div
          v-for="option in categoryOptions"
          :key="option.value"
          class="picker-menu-item"
          :class="{
            'picker-menu-item--selected': selectedCategory === option.value,
          }"
          role="option"
          :aria-selected="selectedCategory === option.value ? 'true' : 'false'"
          @click="selectCategory(option.value)"
          tabindex="0"
          @keydown.enter="selectCategory(option.value)"
          @keydown.space.prevent="selectCategory(option.value)"
        >
          <div class="picker-menu-item-content">
            <span class="picker-menu-label">{{ option.label }}</span>
          </div>
          <img
            v-if="selectedCategory === option.value"
            src="@components/assets/rightIcon.svg"
            alt="selected"
            class="picker-check-icon"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "CURRENCY",
    validator: (value) => ["CURRENCY", "PERCENT", "NUMBER"].includes(value),
  },
  open: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Place Holder",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:modelValue", "update:open", "change"]);

const pickerContainer = ref(null);
const labelElement = ref(null);
const isOpen = ref(props.open);
const isMenuOpen = computed(() => isOpen.value && !props.disabled);

const categoryOptions = [
  { value: "CURRENCY", label: "Currency" },
  { value: "PERCENT", label: "Percent" },
  { value: "NUMBER", label: "Number" },
];

// Local state for selected category - allows component to work standalone
const selectedCategory = ref(props.modelValue);

const categoryLabel = computed(() => {
  const option = categoryOptions.find(
    (opt) => opt.value === selectedCategory.value
  );
  return option ? option.label : "Currency";
});

// Display label - use provided label or default to "Format"
const displayLabel = computed(() => {
  return props.label || "Format";
});

// Placeholder shows when dropdown is open (floating label pattern)

// Sync with parent's modelValue prop changes
watch(
  () => props.modelValue,
  (newVal) => {
    selectedCategory.value = newVal;
  }
);

watch(
  () => props.open,
  (newVal) => {
    isOpen.value = newVal;
  }
);

const toggleDropdown = () => {
  if (props.disabled) {
    return;
  }
  isOpen.value = !isOpen.value;
  emit("update:open", isOpen.value);
};

const closeDropdown = () => {
  isOpen.value = false;
  emit("update:open", false);
};

const selectCategory = (categoryValue) => {
  if (props.disabled) {
    return;
  }
  selectedCategory.value = categoryValue; // Update local state immediately
  emit("update:modelValue", categoryValue);
  emit("change", categoryValue);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (pickerContainer.value && !pickerContainer.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.kpi-format-picker {
  position: relative;
  width: 169px;
  font-family: Inter, sans-serif;
}

.picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.picker-trigger:focus,
.picker-trigger--open {
  border-color: #d6bbfb;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
    0px 0px 0px 4px rgba(158, 119, 237, 0.24);
}

.picker-trigger--disabled {
  background-color: #f9fafb;
  box-shadow: none;
  cursor: not-allowed;
}

.picker-trigger--disabled:focus {
  border-color: #d0d5dd;
  box-shadow: none;
}

.picker-trigger--disabled .picker-label {
  color: #475467;
}

.picker-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.picker-input-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow: hidden;
}

.picker-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.picker-field-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  margin-bottom: 6px;
}

.picker-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: block;
  flex-shrink: 0;
}

.picker-placeholder {
  font-size: 10px;
  color: #667085;
  position: absolute;
  top: 17px;
  left: 11px;
  background: white;
  padding: 0px 4px;
}

.picker-chevron {
  flex-shrink: 0;
  color: #667085 !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.picker-chevron--rotated {
  transform: rotate(180deg);
}

.picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.picker-menu {
  padding: 4px 0;
}

.picker-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 10px 8px;
  margin: 1px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
}

.picker-menu-item:hover {
  background-color: #f9fafb;
}

.picker-menu-item:focus {
  background-color: #f9fafb;
}

.picker-menu-item--selected {
  background-color: #f9fafb;
}

.picker-menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.picker-menu-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.picker-menu-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-check-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}
</style>
